import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  FlightSearchSelectors,
  FlightShopSelectors,
} from "@b2bportal/core-flights";
import dayjs from "dayjs";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import { getPricingMode } from "@/store/flights/shop/store";
import {
  recordUpSellResponse,
  recordUpSellShown,
  type UpSellResponse,
} from "@/tracking/upSell/flightUpSellInteraction";
import { toUpSellPlacement } from "@/tracking/upSell/placement";
import {
  type UpSellChoice,
  type UpSellCommonProperties,
  UpSellRawEventName,
  useEmitRbcRawEvent,
  useEmitRbcRawEventOnce,
} from "@/tracking/upSellEmit";

/**
 * Emitters for the flights up-sell events, with the placement and price tab
 * every one of them carries already filled in.
 *
 * @param isEligible gates the once-per-view `viewed_hotel_up_sell`.
 */
export const useUpSellTracking = (isEligible: boolean) => {
  const { getExperimentVariant } = useExperiment();
  const variant = getExperimentVariant(
    Experiments.UPSELL_STAYS_FROM_FLIGHTS.name
  );
  const rbcPriceTab = useSelector(getPricingMode);
  const { tripId } = useSelector(FlightShopSelectors.selectedTripSelector);

  const tripCategory = useSelector(FlightSearchSelectors.getTripCategory);
  const origin = useSelector(FlightSearchSelectors.getOrigin);
  const destination = useSelector(FlightSearchSelectors.getDestination);
  const departureDate = useSelector(FlightSearchSelectors.getDepartureDate);
  const returnDate = useSelector(FlightSearchSelectors.getReturnDate);
  const adultsCount = useSelector(FlightSearchSelectors.getAdultsCount);
  const childrenCount = useSelector(FlightSearchSelectors.getChildrenCount);
  const infantsInSeat = useSelector(
    FlightSearchSelectors.getInfantsInSeatCount
  );
  const infantsOnLap = useSelector(FlightSearchSelectors.getInfantsOnLapCount);

  const common: UpSellCommonProperties = useMemo(
    () => ({
      up_sell_placement: toUpSellPlacement(variant),
      ...(rbcPriceTab ? { rbc_price_tab: rbcPriceTab } : {}),
    }),
    [variant, rbcPriceTab]
  );

  const emitTapped = useEmitRbcRawEvent(UpSellRawEventName.TappedHotelUpSell);
  const emitModalViewed = useEmitRbcRawEvent(
    UpSellRawEventName.ViewedHotelUpSellModal
  );
  const emitModalCta = useEmitRbcRawEvent(
    UpSellRawEventName.TappedHotelUpSellModalCta
  );

  // Denominator for attach rate: once per review page view, only when eligible.
  useEmitRbcRawEventOnce(
    {
      name: UpSellRawEventName.ViewedHotelUpSell,
      properties: {
        ...common,
        trip_type: tripCategory,
        origin: origin?.label,
        destination: destination?.label,
        departure_date: departureDate ?? undefined,
        return_date: returnDate ?? undefined,
        los:
          departureDate && returnDate
            ? dayjs(returnDate).diff(dayjs(departureDate), "day")
            : undefined,
        adults_count: adultsCount,
        children_count: childrenCount,
        infants_count: (infantsInSeat ?? 0) + (infantsOnLap ?? 0),
      },
    },
    isEligible
  );

  // complete_buy_air reads this back; no event fires from here.
  useEffect(() => {
    if (isEligible) recordUpSellShown(tripId);
  }, [isEligible, tripId]);

  return {
    common,
    trackTapped: useCallback(
      () => emitTapped({ properties: common }),
      [emitTapped, common]
    ),
    trackModalViewed: useCallback(
      () => emitModalViewed({ properties: common }),
      [emitModalViewed, common]
    ),
    trackModalCta: useCallback(
      (choice: UpSellChoice) => {
        emitModalCta({ properties: { ...common, choice } });
        recordUpSellResponse(tripId, choice as unknown as UpSellResponse);
      },
      [emitModalCta, common, tripId]
    ),
    recordResponse: useCallback(
      (response: UpSellResponse) => recordUpSellResponse(tripId, response),
      [tripId]
    ),
  };
};
